export type WhatsappEntityType = 'order' | 'lead'

export type WhatsappComposerConnection = {
  id: string
  label: string
  displayPhoneNumber: string | null
  verifiedName: string | null
  status: 'pending' | 'active'
}

export type WhatsappComposerParameter = {
  id: string
  label: string
  overrideKey: string | null
  value: string
  editable: boolean
}

export type WhatsappComposerTemplate = {
  id: string
  connectionId: string
  name: string
  language: string
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION'
  preview: string
  previewTemplate: string
  parameters: WhatsappComposerParameter[]
}

export type WhatsappComposerData = {
  canSend: boolean
  phone: string
  connections: WhatsappComposerConnection[]
  selectedConnectionId: string | null
  templates: WhatsappComposerTemplate[]
  conversation: {
    id: string
    connectionId: string
    windowOpen: boolean
  } | null
}

export type WhatsappConversationMessage = {
  id: string
  direction: 'in' | 'out'
  type: string
  body: string
  status: string
  sentBy: string
  createdAt: string
}

export type WhatsappAutomationDelivery = {
  id: string
  body: string
  status: string
  createdAt: string
  answered: boolean
}

export type WhatsappOrderConversationSummary = {
  phone: string
  conversationId: string | null
  messages: WhatsappConversationMessage[]
  automations: WhatsappAutomationDelivery[]
}

export type WhatsappActionResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } }

export function getInitialWhatsappChannel(
  connections: readonly WhatsappComposerConnection[],
): 'api' | 'web' {
  return connections.length > 0 ? 'api' : 'web'
}

export function getTemplatesForConnection(
  templates: readonly WhatsappComposerTemplate[],
  connectionId: string,
): WhatsappComposerTemplate[] {
  return templates.filter((template) => template.connectionId === connectionId)
}

export function getTemplateOverrideValues(
  template: WhatsappComposerTemplate | null,
): Record<string, string> {
  if (!template) return {}

  const values: Record<string, string> = {}
  for (const parameter of template.parameters) {
    if (parameter.editable && parameter.overrideKey && !(parameter.overrideKey in values)) {
      values[parameter.overrideKey] = parameter.value
    }
  }
  return values
}

export function renderWhatsappApiPreview(
  template: WhatsappComposerTemplate | null,
  values: Record<string, string>,
): string {
  if (!template) return ''
  return template.previewTemplate.replace(
    /\{\{\s*([a-zA-Z0-9_:-]+)\s*\}\}/g,
    (_match, key: string) => values[key] ?? `{{${key}}}`,
  )
}

export function canSendWhatsappForEntity(input: {
  entityCanWrite: boolean
  whatsappCanWrite: boolean
}): boolean {
  return input.entityCanWrite && input.whatsappCanWrite
}

export function canUseWhatsappFreeform(input: {
  conversation: { connectionId: string; windowOpen: boolean } | null
  selectedConnectionId: string
  entityPhone: string
  recipientPhone: string
}): boolean {
  const entityDigits = input.entityPhone.replace(/\D/g, '')
  const recipientDigits = input.recipientPhone.replace(/\D/g, '')
  return Boolean(
    input.conversation?.windowOpen &&
    input.conversation.connectionId === input.selectedConnectionId &&
    entityDigits &&
    entityDigits === recipientDigits,
  )
}

export function shouldPollWhatsappUnread(
  canReadWhatsapp: boolean,
  visibilityState: 'visible' | 'hidden' | 'prerender',
): boolean {
  return canReadWhatsapp && visibilityState === 'visible'
}

export function whatsappInboxHref(input: {
  conversationId?: string | null
  phone?: string | null
}): string {
  if (input.conversationId) {
    return `/admin/whatsapp?c=${encodeURIComponent(input.conversationId)}`
  }
  return `/admin/whatsapp?phone=${encodeURIComponent(input.phone ?? '')}`
}

export function whatsappMessageStatusLabel(status: string): string {
  switch (status) {
    case 'read':
      return 'Okundu'
    case 'delivered':
      return 'Teslim edildi'
    case 'sent':
      return 'Gönderildi'
    case 'queued':
      return 'Sırada'
    case 'failed':
      return 'Gönderilemedi'
    case 'received':
      return 'Alındı'
    default:
      return status
  }
}
